<script lang="ts" setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import MultiSelectField from '@/components/admin/form/MultiSelectField.vue'
  import SkeletonLoaderInlineField from '@/components/skeleton/SkeletonLoaderInlineField.vue'
  import type { Option as SelectOption } from '@/components/admin/form/SelectField.vue'
  import type { Option as MultiSelectOption } from '@/components/admin/form/MultiSelectField.vue'
  import {
    FormsService,
    QuestionnairesService,
    EmailTemplatesService,
    type TForm,
    type TQuestionnaire,
    type TEmailTemplate,
    type SendClientTemplateEmailActionPayload
  } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'

  // Translation setup
  const { t } = useTranslation({
    en: {
      selectTemplate: 'Select template...',
      selectQuestionnaires: 'Select questionnaires...',
      selectPdfForms: 'Select PDF forms...',
      noTemplatesFound: 'No templates found',
      noQuestionnairesFound: 'No questionnaires found',
      noPdfFormsFound: 'No PDF forms found',
      sendEmailTemplate: 'Send email template',
      toTheClient: 'to the client',
      withQuestionnaireSingular: 'with the questionnaire',
      withQuestionnairePlural: 'with the questionnaires',
      withQuestionnaireSingularCommitted: 'with the questionnaire',
      withQuestionnairesPluralCommitted: 'with the questionnaires',
      andPdfFormSingular: 'and the PDF form',
      andPdfFormPlural: 'and the PDF forms',
      andPdfFormSingularCommitted: 'and the PDF form',
      andPdfFormsPluralCommitted: 'and the PDF forms',
      questionnaireSingular: 'questionnaire',
      questionnairePlural: 'questionnaires',
      pdfFormSingular: 'PDF form',
      pdfFormPlural: 'PDF forms',
      and: 'and',
      emailTemplateSent: 'The email template',
      wassentToClient: 'was sent to the client',

      withSingular: 'with',
      withPlural: 'with'
    },
    de: {
      selectTemplate: 'Vorlage auswählen...',
      selectQuestionnaires: 'Fragebögen auswählen...',
      selectPdfForms: 'PDF-Formulare auswählen...',
      noTemplatesFound: 'Keine Vorlagen gefunden',
      noQuestionnairesFound: 'Keine Fragebögen gefunden',
      noPdfFormsFound: 'Keine PDF-Formulare gefunden',
      sendEmailTemplate: 'Sende die E-Mail-Vorlage',
      toTheClient: 'an den Mandanten.',
      withQuestionnaireSingular: 'mit dem Fragebogen',
      withQuestionnairePlural: 'mit den Fragebögen',
      withQuestionnaireSingularCommitted: 'mit dem Fragebogen',
      withQuestionnairesPluralCommitted: 'mit den Fragebögen',
      andPdfFormSingular: 'und dem PDF-Formular',
      andPdfFormPlural: 'und den PDF-Formularen',
      andPdfFormSingularCommitted: 'und dem PDF-Formular',
      andPdfFormsPluralCommitted: 'und den PDF-Formularen',
      questionnaireSingular: 'Fragebogen',
      questionnairePlural: 'Fragebögen',
      pdfFormSingular: 'PDF-Formular',
      pdfFormPlural: 'PDF-Formulare',
      and: 'und',
      emailTemplateSent: 'Die E-Mail-Vorlage',
      wassentToClient: 'wurde an den Mandanten gesendet.'
    }
  })

  // Define props
  interface Props {
    action: any // TAgentAction type from generated types
    customPayload?: any // Optional custom payload from parent
  }

  const props = defineProps<Props>()

  // Define emits to pass through to parent
  const emit = defineEmits<{
    payloadChanged: [payload: any]
  }>()

  // Reactive state
  const templates = ref<TEmailTemplate[]>([])
  const forms = ref<TForm[]>([])
  const questionnaires = ref<TQuestionnaire[]>([])
  const isLoadingTemplates = ref(false)
  const isLoadingForms = ref(false)
  const isLoadingQuestionnaires = ref(false)
  const selectedTemplateUuid = ref<string>('')
  const selectedQuestionnaireUuids = ref<string[]>([])
  const selectedPdfFormUuids = ref<string[]>([])

  const templateOptions = computed<SelectOption[]>(() => {
    return templates.value.map((template) => ({
      value: template.uuid,
      title: template.name,
      icon: undefined
    }))
  })

  const questionnaireOptions = computed<MultiSelectOption[]>(() => {
    return questionnaires.value.map((questionnaire) => ({
      value: questionnaire.uuid,
      title: questionnaire.title,
      icon: undefined
    }))
  })

  const pdfFormOptions = computed<MultiSelectOption[]>(() => {
    return forms.value.map((form) => ({
      value: form.id, // Forms use id, not uuid
      title: form.title || 'Untitled Form',
      icon: undefined
    }))
  })

  // Computed properties for dynamic text generation
  const selectedTemplate = computed(() => {
    return templates.value.find((template) => template.uuid === selectedTemplateUuid.value)
  })

  const selectedQuestionnaires = computed(() => {
    return questionnaires.value.filter((questionnaire) =>
      selectedQuestionnaireUuids.value.includes(questionnaire.uuid)
    )
  })

  const selectedPdfForms = computed(() => {
    return forms.value.filter((form) => selectedPdfFormUuids.value.includes(form.id))
  })

  const questionnaireText = computed(() => {
    const count = selectedQuestionnaires.value.length
    if (count === 0) return ''
    return count === 1 ? t('questionnaireSingular') : t('questionnairePlural')
  })

  const pdfFormText = computed(() => {
    const count = selectedPdfForms.value.length
    if (count === 0) return ''
    return count === 1 ? t('pdfFormSingular') : t('pdfFormPlural')
  })

  const hasQuestionnaires = computed(() => selectedQuestionnaireUuids.value.length > 0)
  const hasPdfForms = computed(() => selectedPdfFormUuids.value.length > 0)

  // Check if action should show summary (committed, reverted, or has error)
  const shouldShowSummary = computed(
    () =>
      props.action?.is_committed === true ||
      props.action?.is_reverted === true ||
      props.action?.has_commit_error === true ||
      (props.action?.is_commitable === false &&
        (selectedTemplateUuid.value || hasQuestionnaires.value || hasPdfForms.value))
  )

  // Summary text for committed/cancelled/error actions
  const summaryText = computed(() => {
    if (!shouldShowSummary.value || !selectedTemplate.value) return ''

    const templateName = selectedTemplate.value.name
    const questionnaireNames = selectedQuestionnaires.value.map((q) => q.title).join(', ')
    const pdfFormNames = selectedPdfForms.value.map((f) => f.title).join(', ')

    let summaryParts = []

    // Add main content
    summaryParts.push(t('emailTemplateSent'), `"${templateName}"`)

    // Add questionnaires if any
    if (selectedQuestionnaires.value.length > 0) {
      const questionnaireText =
        selectedQuestionnaires.value.length === 1
          ? t('withQuestionnaireSingularCommitted')
          : t('withQuestionnairesPluralCommitted')
      summaryParts.push(questionnaireText, `"${questionnaireNames}"`)
    }

    // Add PDF forms if any
    if (selectedPdfForms.value.length > 0) {
      const pdfFormText =
        selectedPdfForms.value.length === 1
          ? t('andPdfFormSingularCommitted')
          : t('andPdfFormsPluralCommitted')
      summaryParts.push(pdfFormText, `"${pdfFormNames}"`)
    }

    // Always end with standard text
    summaryParts.push(t('wassentToClient'))
    return summaryParts.join(' ')
  })

  // Methods
  const fetchTemplates = async () => {
    isLoadingTemplates.value = true
    try {
      const response = await EmailTemplatesService.emailTemplatesList({ limit: 100 })
      templates.value = response.results || []
    } catch (error) {
      console.error('Error fetching templates:', error)
      templates.value = []
    } finally {
      isLoadingTemplates.value = false
    }
  }

  const fetchForms = async () => {
    isLoadingForms.value = true
    try {
      const response = await FormsService.formsList({
        tag: 'MASTER',
        limit: 100
      })
      forms.value = response.results || []
    } catch (error) {
      console.error('Error fetching forms:', error)
      forms.value = []
    } finally {
      isLoadingForms.value = false
    }
  }

  const fetchQuestionnaires = async () => {
    isLoadingQuestionnaires.value = true
    try {
      const response = await QuestionnairesService.questionnairesList({ limit: 100 })
      questionnaires.value = response.results || []
    } catch (error) {
      console.error('Error fetching questionnaires:', error)
      questionnaires.value = []
    } finally {
      isLoadingQuestionnaires.value = false
    }
  }

  const handleTemplateSelection = (templateUuid: string) => {
    selectedTemplateUuid.value = templateUuid
    emitPayloadChange()
  }

  const handleQuestionnaireSelection = (questionnaireUuids: string[]) => {
    selectedQuestionnaireUuids.value = questionnaireUuids
    emitPayloadChange()
  }

  const handlePdfFormSelection = (pdfFormUuids: string[]) => {
    selectedPdfFormUuids.value = pdfFormUuids
    emitPayloadChange()
  }

  // Emit payload changes to parent
  const emitPayloadChange = () => {
    if (props.action?.payload?.case_uuid) {
      const payload = {
        case_uuid: props.action.payload.case_uuid,
        template_uuid: selectedTemplateUuid.value,
        questionnaire_uuids: selectedQuestionnaireUuids.value,
        pdf_form_uuids: selectedPdfFormUuids.value
      }
      emit('payloadChanged', payload)
    }
  }

  // Initialize selections from action payload
  const initializeFromPayload = (payload: SendClientTemplateEmailActionPayload) => {
    if (!payload) return

    if (payload.template_uuid) {
      selectedTemplateUuid.value = payload.template_uuid
    }
    if (payload.questionnaire_uuids) {
      selectedQuestionnaireUuids.value = [...payload.questionnaire_uuids]
    }
    if (payload.pdf_form_uuids) {
      selectedPdfFormUuids.value = [...payload.pdf_form_uuids]
    }
  }

  // Watch for action payload changes
  watch(
    () => props.action?.payload,
    (newPayload) => {
      initializeFromPayload(newPayload)
    },
    { deep: true }
  )

  // Watch for custom payload changes
  watch(
    () => props.customPayload,
    (newCustomPayload) => {
      if (newCustomPayload) {
        initializeFromPayload(newCustomPayload)
      }
    },
    { deep: true }
  )

  // Initialize component
  onMounted(async () => {
    // Set initial selection from action payload or custom payload
    initializeFromPayload(props.customPayload || props.action?.payload)

    // Fetch available data
    await Promise.all([fetchTemplates(), fetchForms(), fetchQuestionnaires()])

    // Emit initial payload if any values are set
    if (
      selectedTemplateUuid.value ||
      selectedQuestionnaireUuids.value.length > 0 ||
      selectedPdfFormUuids.value.length > 0
    ) {
      emitPayloadChange()
    }
  })
</script>

<template>
  <div>
    <!-- Action Summary (committed, cancelled, reverted, or error) -->
    <div
      v-if="shouldShowSummary"
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ summaryText }}</span>
      </div>
    </div>

    <!-- Action Details (when editable) -->
    <div
      v-else
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ t('sendEmailTemplate') }}</span>

        <!-- Template Selection -->
        <template v-if="isLoadingTemplates">
          <SkeletonLoaderInlineField
            width="140px"
            height="32px"
            class="inline-block"
          />
        </template>
        <template v-else>
          <SelectField
            v-model="selectedTemplateUuid"
            variant="inline-text"
            :options="templateOptions"
            :is-filterable="false"
            :disabled="isLoadingTemplates"
            :placeholder="
              templateOptions.length === 0 ? t('noTemplatesFound') : t('selectTemplate')
            "
            name="template"
            @update:model-value="handleTemplateSelection"
          />
        </template>

        <!-- Questionnaires Section -->
        <template v-if="hasQuestionnaires || isLoadingQuestionnaires">
          <span>
            {{
              selectedQuestionnaireUuids.length === 1
                ? t('withQuestionnaireSingular')
                : t('withQuestionnairePlural')
            }}
          </span>

          <template v-if="isLoadingQuestionnaires">
            <SkeletonLoaderInlineField
              width="160px"
              height="32px"
              class="inline-block"
            />
          </template>
          <template v-else>
            <MultiSelectField
              v-model="selectedQuestionnaireUuids"
              variant="inline-text"
              :options="questionnaireOptions"
              :is-filterable="false"
              :disabled="isLoadingQuestionnaires"
              :placeholder="
                questionnaireOptions.length === 0
                  ? t('noQuestionnairesFound')
                  : t('selectQuestionnaires')
              "
              name="questionnaires"
              @update:model-value="handleQuestionnaireSelection"
            />
          </template>
        </template>

        <!-- PDF Forms Section -->
        <template v-if="hasPdfForms || isLoadingForms">
          <!-- Add "and" connector if we have both questionnaires and PDF forms -->
          <span v-if="hasQuestionnaires || isLoadingQuestionnaires">{{ t('and') }}</span>

          <span>
            {{
              selectedPdfFormUuids.length === 1 ? t('andPdfFormSingular') : t('andPdfFormPlural')
            }}
          </span>

          <template v-if="isLoadingForms">
            <SkeletonLoaderInlineField
              width="150px"
              height="32px"
              class="inline-block"
            />
          </template>
          <template v-else>
            <MultiSelectField
              v-model="selectedPdfFormUuids"
              variant="inline-text"
              :options="pdfFormOptions"
              :is-filterable="false"
              :disabled="isLoadingForms"
              :placeholder="
                pdfFormOptions.length === 0 ? t('noPdfFormsFound') : t('selectPdfForms')
              "
              name="pdfForms"
              @update:model-value="handlePdfFormSelection"
            />
          </template>
        </template>

        <!-- Add "to the client" at the end -->
        <span>{{ t('toTheClient') }}</span>

        <!-- Show selection areas when nothing is selected yet -->
        <template
          v-if="!hasQuestionnaires && !isLoadingQuestionnaires && !hasPdfForms && !isLoadingForms"
        >
          <span>{{ t('withQuestionnairePlural') }}</span>
          <MultiSelectField
            v-model="selectedQuestionnaireUuids"
            variant="inline-text"
            :options="questionnaireOptions"
            :is-filterable="false"
            :disabled="isLoadingQuestionnaires"
            :placeholder="
              questionnaireOptions.length === 0
                ? t('noQuestionnairesFound')
                : t('selectQuestionnaires')
            "
            name="questionnaires"
            @update:model-value="handleQuestionnaireSelection"
          />
          <span>{{ t('and') }}</span>
          <span>{{ t('andPdfFormPlural') }}</span>
          <MultiSelectField
            v-model="selectedPdfFormUuids"
            variant="inline-text"
            :options="pdfFormOptions"
            :is-filterable="false"
            :disabled="isLoadingForms"
            :placeholder="pdfFormOptions.length === 0 ? t('noPdfFormsFound') : t('selectPdfForms')"
            name="pdfForms"
            @update:model-value="handlePdfFormSelection"
          />
          <span>{{ t('toTheClient') }}</span>
        </template>
      </div>
    </div>
  </div>
</template>
